import { Howl, Howler } from 'howler';
import store from 'store';
import { setPlaying } from 'actions/audioActions';
import { resolveOfflineAssetUrlSync } from 'mobile-modules/offline';

const generateEvent = (detail) => {
  const event = new CustomEvent('audioEnded', { detail });
  document.dispatchEvent(event);
};

const changeStoreState = (...params) => {
  store.dispatch(setPlaying(...params));
};

class AudioPlayer {
  constructor() {
    this.preloadRequests = new Map();
    this._pendingUnlockSrc = '';
    this._isMobileRuntime = Boolean(
      window.Capacitor?.isNativePlatform?.() ||
      window.Capacitor?.getPlatform?.() === 'ios' ||
      window.Capacitor?.getPlatform?.() === 'android'
    );

    if (this._isMobileRuntime) {
      ['pointerdown', 'touchend', 'keydown'].forEach((eventName) => {
        document.addEventListener(eventName, this.unlockAudioContext, {
          passive: true,
        });
      });
    }

    const objectURL = URL.createObjectURL(new Blob([]));

    const sound = new Howl({
      src: [objectURL],
      format: 'mp3',
    });

    if (Howler.ctx && Howler.ctx.state && Howler.ctx.state === 'suspended') {
      Howler.ctx.resume()
        .then(() => sound.unload())
        .catch(() => sound.unload());
    }
  }

  isDebugEnabled = () => (
    store.getState()?.common?.config?.customContentSettings?.mobileWrapper?.audioDebugMode === true
  );

  reportAudioDiagnostic = (level, stage, src, event) => {
    const diagnostic = {
      stage,
      src: Array.isArray(src) ? src : [src],
      event: event?.message || event || '',
      contextState: Howler.ctx?.state || 'unknown',
      timestamp: new Date().toISOString(),
    };
    const logger = level === 'error' ? console.error : console.info;
    logger('%c[FLC mobile audio]', 'color:#c62828;font-weight:bold', diagnostic);

    if (level === 'error') {
      try {
        window.localStorage.setItem(
          'flc.mobile.lastAudioError',
          JSON.stringify(diagnostic)
        );
      } catch (storageError) {
        console.error('[FLC mobile audio] Unable to persist diagnostic', storageError);
      }
    }
  };

  retryPendingAudio = () => {
    if (!this._pendingUnlockSrc || !this.sound) return;

    const pendingSrc = this._pendingUnlockSrc;
    this._pendingUnlockSrc = '';
    this.sound.play();
    changeStoreState(pendingSrc, true);
  };

  unlockAudioContext = (userEvent) => {
    if (!this._isMobileRuntime || !Howler.ctx) {
      return;
    }

    if (Howler.ctx.state !== 'suspended') {
      if (userEvent) this.retryPendingAudio();
      return;
    }

    Howler.ctx.resume()
      .then(() => {
        if (this.isDebugEnabled()) {
          this.reportAudioDiagnostic(
            'info',
            'context-resumed',
            this._pendingUnlockSrc || this._src || '',
            ''
          );
        }

        this.retryPendingAudio();
      })
      .catch((error) => {
        this.reportAudioDiagnostic(
          'error',
          'context-resume-failed',
          this._pendingUnlockSrc || this._src || '',
          error
        );
      });
  };

  preload(src) {
    const resolvedSrc = resolveOfflineAssetUrlSync(src);
    if (!resolvedSrc || typeof fetch !== 'function') return Promise.resolve(false);
    if (this.preloadRequests.has(resolvedSrc)) return this.preloadRequests.get(resolvedSrc);

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = window.setTimeout(() => controller && controller.abort(), 12000);
    const request = fetch(resolvedSrc, {
      cache: 'force-cache',
      credentials: 'omit',
      ...(controller ? { signal: controller.signal } : {}),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Audio preload failed: ${response.status}`);
        return response.arrayBuffer();
      })
      .then(() => true)
      .catch((error) => {
        console.warn('[FLC audio] Preload failed', src, error?.message || error);
        return false;
      })
      .finally(() => {
        window.clearTimeout(timeout);
        this.preloadRequests.delete(resolvedSrc);
      });

    this.preloadRequests.set(resolvedSrc, request);
    return request;
  }

  async preloadMany(sources, concurrency = 4) {
    const queue = [...new Set((Array.isArray(sources) ? sources : [sources]).filter(Boolean))];
    if (!queue.length) return [];

    const results = new Array(queue.length);
    let nextIndex = 0;
    const worker = async () => {
      while (nextIndex < queue.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await this.preload(queue[index]);
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(concurrency, queue.length) }, () => worker())
    );
    return results;
  }

  play() {
    // console.log('Started', this._src)
    this.sound.play();
    if (this._isMobileRuntime && this.isDebugEnabled()) {
      this.reportAudioDiagnostic('info', 'play-request', this._src, '');
    }
    changeStoreState(this._src, true);
  }

  playSrc(src, options = {}) {
    // console.log('Setup', src)
    const resolvedSrc = Array.isArray(src)
      ? src.map(resolveOfflineAssetUrlSync)
      : resolveOfflineAssetUrlSync(src);
    if (this._src === src) {
      // SRC already set
      this.sound.stop();
      this.play();
    } else {
      // Remove previous sound
      this.sound && this.sound.unload();

      this._src = src;

      const howlOptions = {
        src: Array.isArray(resolvedSrc) ? resolvedSrc : [resolvedSrc],
        // WKWebView can leave WebAudio suspended/interrupted even when the
        // remote MP3 is valid. Native mobile uses the HTML5 Audio backend;
        // desktop keeps the existing WebAudio behavior unchanged.
        html5: this._isMobileRuntime,
        onload: () => {
          if (this._isMobileRuntime && this.isDebugEnabled()) {
            this.reportAudioDiagnostic('info', 'loaded', src, '');
          }

          // Desktop keeps the original start-on-load flow. Native playback is
          // queued immediately below while the initiating tap is still active.
          if (!this._isMobileRuntime) this.play();
        },
        onplay: () => {
          this._pendingUnlockSrc = '';
        },
        onend: () => {
          generateEvent({ src });
          options.onSuccessEnd && options.onSuccessEnd();
          options.onEnd && options.onEnd();

          changeStoreState(this._src, false);
        },
        onloaderror: (soundId, event) => {
          console.log('Load audio error', src, event);

          if (this._isMobileRuntime) {
            this.reportAudioDiagnostic('error', 'load-error', src, event);
          }

          generateEvent({
            src,
            errorEvent: event,
            message: 'Error while loading audio file',
          });
          options.onEnd && options.onEnd();
          changeStoreState(this._src, false);
        },
        onplayerror: (soundId, event) => {
          console.log('Play audio error', event);
          if (this._isMobileRuntime) {
            this._pendingUnlockSrc = src;
            this.reportAudioDiagnostic('error', 'play-error', src, event);
            if (Howler.ctx?.state === 'suspended') this.unlockAudioContext();
            changeStoreState(this._src, false);
            return;
          }
          generateEvent({
            src,
            errorEvent: event,
            message: 'Error while playing audio file',
          });
          options.onEnd && options.onEnd();
          changeStoreState(this._src, false);
        },
      };

      if (options.format) {
        howlOptions.format = options.format;
      }

      // Create new sound
      this.sound = new Howl(howlOptions);

      // Calling play immediately lets Howler queue the request until loading
      // completes without losing the iOS user-activation window.
      if (this._isMobileRuntime) this.play();
    }
  }

  toggleSequence(array, options = {}) {
    const lastIdx = array.length - 1;

    let currentIdx = 0;

    const setupNext = () => {
      if (currentIdx === lastIdx) {
        this.togglePlaySrc(array[currentIdx++], options);
      } else {
        this.togglePlaySrc(array[currentIdx++], {
          ...options,
          onEnd: undefined,
          onSuccessEnd: setupNext,
        });
      }
    };

    setupNext();
  }

  togglePlaySrc(src, options) {
    if (this._src === src) {
      this.togglePlay();
    } else {
      this.playSrc(src, options);
    }
  }

  togglePlay() {
    if (!this.sound) return;
    this.sound.playing() ? this.stop() : this.play();
  }

  stop(src) {
    if (!this.sound) return;

    // If src was passed then stop audio only if src is match
    if (!src || src === this._src) {
      this.sound.stop();
      changeStoreState(this._src, false);
    }
  }

  getCurrentSrc() {
    return this._src;
  }

  getCurrentlyPlaying() {
    if (!this.sound) return false;

    return this.sound.playing();
  }
}

window.audioPlayer = new AudioPlayer();
